import type { Dot, HillChart, HillChartListResponse, UpdateDotBody } from '@hillchart/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { hillsApi } from './api';
import { queryKeys } from './query-keys';

interface MoveDotVars {
  dotId: string;
  body: UpdateDotBody;
}

interface MoveDotContext {
  /** Snapshot of the hill cache entry before the optimistic patch. */
  previousHill: HillChart | undefined;
}

/**
 * Apply an `UpdateDotBody` to a full `Dot`, preserving strict optional types.
 *
 * A naïve `{ ...d, ...body }` is rejected by TypeScript under
 * `exactOptionalPropertyTypes`: `UpdateDotBody` fields are `x?: number`, which
 * is representable as `undefined`, but `Dot.x` is a concrete `number`. Spread
 * would allow an explicit `undefined` to overwrite a real value. We sidestep
 * it by only copying keys whose value is actually defined.
 *
 * The runtime semantics are unchanged — Zod's `UpdateDotBody` refinement
 * forbids an all-undefined body, so at least one key always lands here.
 */
function mergeDotPatch(dot: Dot, patch: UpdateDotBody): Dot {
  const next: Dot = { ...dot };
  if (patch.x !== undefined) next.x = patch.x;
  if (patch.color !== undefined) next.color = patch.color;
  if (patch.completed !== undefined) next.completed = patch.completed;
  return next;
}

/**
 * Mutation for updating a single dot on a hill chart, with optimistic UI.
 *
 * The sequence is:
 *   1. `onMutate`  — cancel in-flight hill fetches, snapshot the cache,
 *                    patch the dot in place so the drag lands instantly
 *                    even before the PATCH hits the wire.
 *   2. `onSuccess` — replace the optimistic dot with the server-returned
 *                    dot. That version carries the authoritative
 *                    `lastMovedAt` / `lastMovedByUserId`, which we can't
 *                    predict client-side and which the WS echo-skip
 *                    (Phase D next step) will use to decide what to ignore.
 *   3. `onError`   — restore the snapshot. We never leave the UI showing
 *                    a state that won't be persisted.
 *
 * The hook is generic over `UpdateDotBody` (x / color / completed) rather
 * than x-only, so the same path covers the color picker and the "mark
 * done" toggle that land in later phases.
 *
 * Note on `mutationKey`: we scope it per-hill, not per-dot. Dragging two
 * different dots on the same hill should still be serialised by TanStack
 * Query's `cancelQueries` cycle — the shared key guarantees onMutate of
 * mutation #2 cancels the refetch spawned by mutation #1 before reading
 * the cache.
 */
export function useMoveDot(hillId: string) {
  const qc = useQueryClient();
  const hillKey = queryKeys.hill(hillId);

  return useMutation<Dot, Error, MoveDotVars, MoveDotContext>({
    mutationKey: [...hillKey, 'move-dot'],
    mutationFn: ({ dotId, body }) => hillsApi.updateDot(hillId, dotId, body),

    onMutate: async ({ dotId, body }) => {
      // Without this, an in-flight hill refetch can resolve *after* our
      // optimistic patch and clobber it back to the server's stale state.
      await qc.cancelQueries({ queryKey: hillKey });

      const previousHill = qc.getQueryData<HillChart>(hillKey);
      if (previousHill !== undefined) {
        qc.setQueryData<HillChart>(hillKey, {
          ...previousHill,
          dots: previousHill.dots.map((d) => (d.id === dotId ? mergeDotPatch(d, body) : d)),
        });
      }
      return { previousHill };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousHill !== undefined) {
        qc.setQueryData(hillKey, context.previousHill);
      }
    },

    onSuccess: (updatedDot) => {
      // Server-authoritative merge: replace the optimistic dot with the
      // real one. Any fields we didn't touch (color when moving x, etc.)
      // are already correct because the backend returns the full dot.
      qc.setQueryData<HillChart>(hillKey, (prev) => {
        if (prev === undefined) return prev;
        return {
          ...prev,
          dots: prev.dots.map((d) => (d.id === updatedDot.id ? updatedDot : d)),
        };
      });

      // Keep the hills-list cache in sync so the card thumbnail on `/hills`
      // reflects the move when the user navigates back. Without this, the
      // list query's 30s staleTime would serve its pre-move payload and
      // the <HillPreview> dots would stay at their old positions until a
      // refetch fires. No last-write-wins guard needed here — this is the
      // authoritative response to *our* PATCH, so its `lastMovedAt` is by
      // definition the newest we know about.
      qc.setQueryData<HillChartListResponse>(queryKeys.hillsList(), (prev) => {
        if (prev === undefined) return prev;
        return {
          ...prev,
          hills: prev.hills.map((h) =>
            h.id !== hillId
              ? h
              : {
                  ...h,
                  dots: h.dots.map((d) => (d.id === updatedDot.id ? updatedDot : d)),
                },
          ),
        };
      });
    },
  });
}
